<template>
  <div ref="container" class="cell-container" :style="`width: ${props.width}px; height: ${props.height}px;`">
    <div class="overlay-info" v-if="loading">正在构建细胞结构... {{ progress }}%</div>
  </div>
</template>

<script setup>
  import { ref, onMounted, onBeforeUnmount } from 'vue';
  import * as THREE from 'three';


  const props = defineProps({
    /*  query: {
       type: Object,
       required: true
     },
     depth: {
       type: Number,
       default: 0
     } */

    width: {
      type: Number,
      default: 130
    },
    height: {
      type: Number,
      default: 130
    }

  });

  const container = ref(null);
  const loading = ref(true);
  const progress = ref(0);

  // 资源配置：请确保路径与你本地下载的文件一致
  const maskConfigs = [
    { name: 'cytoplasm', url: '/textures/subcell_masks/cytoplasm0001.png', color: 0x1a3a5a, size: 0.14, z: -2 },
    { name: 'membrane', url: '/textures/subcell_masks/membrane0001.png', color: 0x44aaff, size: 0.12, z: -1 },
    { name: 'mito', url: '/textures/subcell_masks/mito0001.png', color: 0x00ffcc, size: 0.08, z: 0.5 },
    { name: 'er', url: '/textures/subcell_masks/er0001.png', color: 0x3399ff, size: 0.08, z: 1.2 },
    { name: 'nucleus', url: '/textures/subcell_masks/nucleus0001.png', color: 0xffffff, size: 0.07, z: 2.5 },
    { name: 'vesicles', url: '/textures/subcell_masks/vesicles0001.png', color: 0xffcc00, size: 0.05, z: 1.8 },
    { name: 'golgi', url: '/textures/subcell_masks/golgi0001.png', color: 0xff9900, size: 0.05, z: 2 },
  ];

  let renderer, scene, camera, animationId;
  const cellParts = {}; // 存储各个部位的点云对象

  // 核心逻辑：解析图片像素并生成点云
  const createPointCloud = async (config) => {
    return new Promise((resolve) => {
      const loader = new THREE.TextureLoader();
      loader.load(config.url, (texture) => {
        const img = texture.image;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
        const positions = [];
        const colors = [];
        const pColor = new THREE.Color(config.color);

        // 步长控制密度，如果点太多可以调大这个值 (比如 2 或 3)
        const step = 2;

        for (let y = 0; y < canvas.height; y += step) {
          for (let x = 0; x < canvas.width; x += step) {
            const alpha = imageData[(y * canvas.width + x) * 4 + 3];
            if (alpha > 150) { // 阈值判定
              const px = (x / canvas.width) * 2 - 1;
              const py = -(y / canvas.height) * 2 + 1;

              // 基础坐标 + 微小随机扰动营造“云”感
              positions.push(
                px * 15 + (Math.random() - 0.5) * 0.2,
                py * 15 + (Math.random() - 0.5) * 0.2,
                config.z + (Math.random() - 0.5) * 0.5
              );
              colors.push(pColor.r, pColor.g, pColor.b);
            }
          }
        }

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));

        /*  const material = new THREE.PointsMaterial({
           size: config.size,
           vertexColors: true,
           transparent: true,
           opacity: 0.8,
           blending: THREE.AdditiveBlending,
           depthWrite: false,
         }); */

        const material = new THREE.PointsMaterial({
          size: config.size * 1.5, // 稍微调大一点点，亮色背景需要更扎实的点
          vertexColors: true,
          transparent: true,
          opacity: 0.8,            // 提高不透明度
          blending: THREE.NormalBlending, // 重要：改回普通混合，这样颜色才能在白背景上显现
          depthWrite: true,
          sizeAttenuation: true    // 开启尺寸衰减，增加空间深度感
        });

        const points = new THREE.Points(geometry, material);
        resolve(points);
      });
    });
  };

  const initThree = async () => {
    // 1. 场景与渲染器
    scene = new THREE.Scene();
    camera = new THREE.PerspectiveCamera(45, container.value.clientWidth / container.value.clientHeight, 0.1, 1000);
    camera.position.z = 27;

    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(container.value.clientWidth, container.value.clientHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    // renderer.setClearColor('#efefef')
    container.value.appendChild(renderer.domElement);

    // 2. 依次加载并生成点云
    for (let i = 0; i < maskConfigs.length; i++) {
      const points = await createPointCloud(maskConfigs[i]);
      cellParts[maskConfigs[i].name] = points;
      scene.add(points);
      progress.value = Math.round(((i + 1) / maskConfigs.length) * 100);
    }

    loading.value = false;

    // 3. 动画循环
    const animate = () => {
      animationId = requestAnimationFrame(animate);
      // 整个细胞缓缓旋转
      scene.rotation.y += 0.002;
      renderer.render(scene, camera);
    };
    animate();
  };

  // 处理窗口缩放
  const handleResize = () => {
    if (!container.value) return;
    camera.aspect = container.value.clientWidth / container.value.clientHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(container.value.clientWidth, container.value.clientHeight);
  };

  onMounted(() => {
    initThree();
    window.addEventListener('resize', handleResize);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('resize', handleResize);
    cancelAnimationFrame(animationId);
    // 释放显存
    scene.traverse((obj) => {
      if (obj.isPoints) {
        obj.geometry.dispose();
        obj.material.dispose();
      }
    });
    renderer.dispose();
  });

  // 暴露给父组件的方法：用于高亮特定部位
  defineExpose({
    highlight: (name) => {
      Object.keys(cellParts).forEach(key => {
        cellParts[key].material.opacity = (key === name) ? 1.0 : 0.2;
      });
    }
  });
</script>

<style scoped>
  .cell-container {
    width: 130px;
    height: 130px;
    position: relative;
    overflow: hidden;
    margin: 0 auto;
  }

  .overlay-info {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: #44aaff;
    font-family: 'Courier New', Courier, monospace;
    text-transform: uppercase;
    letter-spacing: 2px;
  }
</style>